import React, { useEffect, useState, useRef } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid2,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AppNoFocusButton from "../../../../../views/components/common/buttons/AppNoFocusButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { Download } from "@mui/icons-material";
import { SortType } from "../../../../../models/SortType";
import totalRevenueicon from "../../../../../assets/total_revenue.png";
import revenueIcon from "../../../../../assets/revenue.png";
import FGIcon from "../../../../../assets/F&G std.png";
import f2fmeetingIcon from "../../../../../assets/f2fmeeting.png";
import inboundIcon from "../../../../../assets/inbound.png";
import outboundIcon from "../../../../../assets/outbound.png";
import avgDiscountIcon from "../../../../../assets/AvgDiscount.png";
import callIcon from "../../../../../assets/call.png";
import salesCycleIcon from "../../../../../assets/SalesCycle.png";
import CustomCircularProgress, {
  getGradientColorAtScore,
} from "../../../../../views/components/common/progressBars/CustomCircularProgress";
import ColumnData from "../../components/tables/DashboardSalesPipelinesTable";
import {
  parseQueryParamsForSalesAnalyticsFilters,
  SalesAnalyticsFilters,
  SalesAnalyticsSelectedFilters,
} from "../../../../../models/Dashboard/SalesDashboard/SalesAnalyticsFilters";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { pipelineStages } from "../../../../../models/Dashboard/SalesDashboard/SalesAnalytics";
import { formatNumber } from "../../../../../views/components/common/functions/formatDate";
import OwnerSalesRowTable from "../../components/analytics/OwnerSalesRowTable";
import { CurrencyHelper } from "../../../../../utils/CurrencyHelper";
import SalesIncentivesRowTable from "../../components/analytics/SalesIncentivesRowTable";
import AdminTeamsAPI from "../../../api/AdminTeamsAPI";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { AdminTeam } from "../../../../../models/AdminTeam/AdminTeam";

const SALES_ANALYTICS_FILTERS_KEY = "SALES_ANALYTICS_FILTERS_KEY";
const DOMESTIC_TEAM_NAME = "domestic";

function resolveDomesticTeamFilter(
  subTeams: AdminTeam[],
): FilterValue | undefined {
  const team = subTeams.find(
    (item) => item.name.trim().toLowerCase() === DOMESTIC_TEAM_NAME,
  );
  if (!team) return undefined;
  return { id: team.id.toString(), value: team.name };
}

function resolveDefaultTeamIds(subTeams: AdminTeam[]): FilterValue[] {
  const domesticTeam = resolveDomesticTeamFilter(subTeams);
  return domesticTeam ? [domesticTeam] : [];
}

const adminTeamsAPI = new AdminTeamsAPI();

const SalesAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();
  const { userStore, salesAnalyticsDashboardStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    [],
  );
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getResetPipelinePages = () => {
    const resetPages: { [key: string]: number } = {};
    pipelineStages.forEach((stage) => {
      resetPages[`leads_page_no_${stage}`] = 1;
      resetPages[`deals_page_no_${stage}`] = 1;
    });
    return resetPages;
  };

  // Memoize initial filters
  const initialFilters: SalesAnalyticsFilters = React.useMemo(
    () =>
      parseQueryParamsForSalesAnalyticsFilters(
        localStorage.getItem(SALES_ANALYTICS_FILTERS_KEY)
          ? JSON.parse(localStorage.getItem(SALES_ANALYTICS_FILTERS_KEY)!)
          : null,
        searchParams,
      ),
    [searchParams],
  );

  const [filters, setFilters] = useState<SalesAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<SalesAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  // Update and persist filters
  const updateAndPersistFilters = React.useCallback(
    (newFilters: SalesAnalyticsFilters) => {
      localStorage.setItem(
        SALES_ANALYTICS_FILTERS_KEY,
        JSON.stringify(newFilters),
      );
      setFilters(newFilters);
      setSelectedFilters({
        ...newFilters,
        start_date: newFilters.start_date.format("YYYY-MM-DD"),
        end_date: newFilters.end_date.format("YYYY-MM-DD"),
      });
      setSelectedFiltersData(
        (newFilters.owner_ids || []).map((owner) => ({
          id: "owner_ids",
          value: owner.value,
        })),
      );
    },
    [],
  );

  // Centralized data fetching
  const fetchAllAnalyticsAndPipeline = React.useCallback(
    async (filterObj: SalesAnalyticsSelectedFilters) => {
      setLoading(true);
      try {
        await salesAnalyticsDashboardStore.getSalesAnalyticsData(filterObj);
        await salesAnalyticsDashboardStore.fetchAllPipelineData(filterObj);
      } catch (error) {
        console.error("Error fetching all analytics and pipeline data", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salesAnalyticsDashboardStore],
  );

  const fetchIncentivesData = async () => {
    try {
      await salesAnalyticsDashboardStore.fetchIncentivesData(selectedFilters);
    } catch (error) {
      salesAnalyticsDashboardStore.incentivesData = [];
      console.error("Error fetching incentives data", error);
    }
  };

  // Effects
  useEffect(() => {
    updateAndPersistFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const initializeDashboard = async () => {
      await fetchSubTeams();
      const persistedTeamIds = filters.team_ids ?? [];
      const teamIds =
        persistedTeamIds.length > 0
          ? persistedTeamIds
          : resolveDefaultTeamIds(salesAnalyticsDashboardStore.subTeams);

      if (persistedTeamIds.length === 0 && teamIds.length > 0) {
        updateAndPersistFilters({ ...filters, team_ids: teamIds });
      }

      await fetchAllAnalyticsAndPipeline({
        ...selectedFilters,
        team_ids: teamIds,
      });
      fetchOwners();
      fetchIncentivesData();
    };
    initializeDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });
    Object.entries(salesAnalyticsDashboardStore.pipelinePages).forEach(
      ([key, value]) => {
        newSearchParams.set(key, value.toString());
      },
    );
    setSearchParams(newSearchParams);
  }, [
    selectedFilters,
    salesAnalyticsDashboardStore.pipelinePages,
    setSearchParams,
  ]);

  useEffect(() => {
    const pages: { [key: string]: number } = {};
    pipelineStages.forEach((stage) => {
      const leadsPage = searchParams.get(`leads_page_no_${stage}`);
      const dealsPage = searchParams.get(`deals_page_no_${stage}`);
      if (leadsPage) pages[`leads_page_no_${stage}`] = parseInt(leadsPage);
      if (dealsPage) pages[`deals_page_no_${stage}`] = parseInt(dealsPage);
    });
    if (Object.keys(pages).length > 0) {
      salesAnalyticsDashboardStore.setPipelinePages(pages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handlePipelinePageChange = async (
    type: "leads" | "deals",
    stage: number,
    newPage: number,
  ) => {
    setLoading(true);
    salesAnalyticsDashboardStore.updatePipelinePage(type, stage, newPage);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(`${type}_page_no_${stage}`, newPage.toString());
    setSearchParams(newSearchParams);
    try {
      if (type === "leads") {
        await salesAnalyticsDashboardStore.fetchLeadsPipelineData(
          selectedFilters,
          stage,
        );
      } else {
        await salesAnalyticsDashboardStore.fetchDealsPipelineData(
          selectedFilters,
          stage,
        );
      }
    } catch (error) {
      showError("Error", "Failed to fetch pipeline data");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    const resetPages = getResetPipelinePages();
    salesAnalyticsDashboardStore.replacePipelinePages(resetPages);
    const appliedFilters = {
      ...selectedFilters,
      page: 1,
      limit: 30,
      search: "",
      sortBy: "created_date",
      sortType: SortType.DESC,
    };
    updateAndPersistFilters({
      ...filters,
      page: 1,
      limit: 30,
      search: "",
      sortBy: "created_date",
      sortType: SortType.DESC,
    });
    fetchAllAnalyticsAndPipeline(appliedFilters);
    fetchIncentivesData();
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      updateAndPersistFilters({
        ...filters,
        start_date: range.startDate!,
        end_date: range.endDate!,
      });
    }
  };

  const handleTeamToggle = (teamId: string, teamName: string) => {
    setFilters((prev) => {
      const current = prev.team_ids || [];
      const isSelected = current.some((team) => team.id === teamId);
      return {
        ...prev,
        team_ids: isSelected
          ? current.filter((team) => team.id !== teamId)
          : [...current, { id: teamId, value: teamName }],
      };
    });
  };

  const handleTeamFilterRemove = async (teamId: string) => {
    const newTeamIds =
      filters.team_ids?.filter((team) => team.id !== teamId) || [];
    updateAndPersistFilters({
      ...filters,
      team_ids: newTeamIds,
    });
    salesAnalyticsDashboardStore.replacePipelinePages(getResetPipelinePages());
    await fetchAllAnalyticsAndPipeline({
      ...selectedFilters,
      team_ids: newTeamIds,
    });
  };

  const handleRemoveFilter = async (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const newOwnerIds =
      filters.owner_ids?.filter(
        (item) => item.value !== filterToRemove.value,
      ) || [];

    updateAndPersistFilters({
      ...filters,
      owner_ids: newOwnerIds,
    });
    salesAnalyticsDashboardStore.replacePipelinePages(getResetPipelinePages());
    await fetchAllAnalyticsAndPipeline({
      ...selectedFilters,
      owner_ids: newOwnerIds,
    });
  };

  const handleClearAllFilters = async () => {
    const defaultTeamIds = resolveDefaultTeamIds(
      salesAnalyticsDashboardStore.subTeams,
    );
    updateAndPersistFilters({
      ...initialFilters,
      owner_ids: [],
      team_ids: defaultTeamIds,
    });
    salesAnalyticsDashboardStore.replacePipelinePages(getResetPipelinePages());
    await fetchAllAnalyticsAndPipeline({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
      team_ids: defaultTeamIds,
      owner_ids: [],
    });
  };

  const fetchOwners = async () => {
    try {
      await salesAnalyticsDashboardStore.getOwners();
    } catch (error) {
      showError("Error", "Failed to fetch owners");
    }
  };

  const fetchSubTeams = async () => {
    try {
      await salesAnalyticsDashboardStore.getSubTeams(2);
    } catch (error) {
      console.error("Error fetching sub teams", error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      await salesAnalyticsDashboardStore
        .downloadTargetsTemplate()
        .then((response) => {
          if (response.data) {
            const downloadUrl = window.URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = response.filename
              ? response.filename
              : `owner_targets_template.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
          } else {
            return showError(
              "Error Occurred",
              "Failed to download targets template.",
            );
          }
        })
        .catch((err) => {
          showError(
            "Error Occurred",
            err?.message ||
              "Failed to download targets template, please try again later",
          );
        });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    try {
      setUploading(true);
      const response = await salesAnalyticsDashboardStore.uploadTargets(file);
      const successMessage =
        response?.result?.message ||
        `Successfully uploaded ${response?.result?.inserted || 0} targets`;
      showSuccess(successMessage);

      await fetchAllAnalyticsAndPipeline(selectedFilters);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to upload targets";
      const errorDetails = error?.response?.data?.result;
      if (Array.isArray(errorDetails) && errorDetails.length > 0) {
        showError(
          "Validation Errors",
          `${errorMessage}\n${errorDetails.join("\n")}`,
        );
      } else {
        showError("Error", errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  // const handleOwnerSearch = (searchText: string) => {
  //   if (searchText.length > 2) {
  //     salesAnalyticsDashboardStore.getOwners(searchText);
  //   }
  // };

  const iconMock = [
    revenueIcon,
    FGIcon,
    f2fmeetingIcon,
    inboundIcon,
    outboundIcon,
  ];

  const pieColors = [
    theme.palette.orange?.main,
    theme.palette.goldenYellow?.main,
    theme.palette.mintGreen?.main,
    theme.palette.customColors.pinkDark,
    theme.palette.skyBlue?.main,
  ];

  const pieColors1 = [
    theme.palette.blushPink?.light,
    theme.palette.customColors.lightYellow,
    theme.palette.customColors.greensuperLight,
    theme.palette.customColors.pinkLight,
    theme.palette.customColors.lightBlue,
  ];

  const stepImagesInventoryOpsTat = [callIcon, salesCycleIcon, avgDiscountIcon];

  const performanceGradientColors = [
    theme.palette.red.main,
    theme.palette.orange.main,
    theme.palette.green.main,
  ];

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_SALES_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Grid2>
            <Box>
              <Paper
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 2,
                  boxShadow:
                    "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 0,
                    borderRadius: 2,
                    borderColor: "divider",
                    // Allow dropdowns (like the date picker) to render outside
                    // without being clipped by this container.
                    overflow: "visible",
                  }}
                >
                  <Box
                    onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 0.5,
                      cursor: "pointer",
                      borderBottom: isFiltersExpanded ? "1px solid" : "none",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <CustomTypography variant="heading6">
                      Filters
                    </CustomTypography>
                    <AppNoFocusButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsFiltersExpanded(!isFiltersExpanded);
                      }}
                      sx={{
                        minWidth: 32,
                        minHeight: 32,
                        padding: 0.5,
                        color: theme.palette.text.secondary,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      {isFiltersExpanded ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </AppNoFocusButton>
                  </Box>
                  <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          alignItems: "flex-end",
                          gap: 1.5,
                        }}
                      >
                        <Grid2
                          container
                          spacing={2}
                          columns={12}
                          sx={{ width: "100%" }}
                        >
                          <Grid2
                            size={{ lg: 3, md: 3, sm: 6, xs: 12 }}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {/* <CustomTypography
                            variant="heading7"
                            color={
                              theme.palette.customColors.tableGrayHeadingColor
                            }
                            style={{ marginBottom: 10, fontSize: "0.8rem" }}
                          >
                            Date Range
                          </CustomTypography> */}
                            <DateRangePickerNew
                              onRangeSelect={handleRangeSelect}
                              startDate={filters.start_date}
                              endDate={filters.end_date}
                            />
                          </Grid2>
                          <Grid2 size={{ lg: 3, md: 3, sm: 6, xs: 12 }}>
                            <AppSearchDropdownMulti
                              title="Select Owner(s)"
                              isMultiSelect={true}
                              list={
                                salesAnalyticsDashboardStore?.owners?.map(
                                  (item) => ({
                                    id: item.id.toString(),
                                    value: item.value,
                                  }),
                                ) || []
                              }
                              value={filters.owner_ids?.map((item) => ({
                                id: item.id.toString(),
                                value: item.value,
                              }))}
                              local={true}
                              fontSize={"12px"}
                              handleSelect={(items) => {
                                setFilters((prev) => ({
                                  ...prev,
                                  owner_ids: items,
                                }));
                              }}
                            />
                            {/* <AppDropDown
                            title="Select Owner(s)"
                            showPlaceholder={false}
                            isMultiSelect={true}
                            valuesList={
                              filters.owner_ids?.map((item) => item.id) || []
                            }
                            list={salesAnalyticsDashboardStore?.owners ?? []}
                            fontSize={"12px"}
                            onMultiSelectionChange={(ids) => {
                              setFilters((prev) => ({
                                ...prev,
                                owner_ids:
                                  salesAnalyticsDashboardStore?.owners?.filter(
                                    (item) => ids.includes(item.id),
                                  ) || [],
                              }));
                            }}
                          /> */}
                          </Grid2>
                          <Grid2 size={{ lg: 3, md: 3, sm: 6, xs: 12 }}></Grid2>
                          <Grid2 size={{ lg: 3, md: 3, sm: 6, xs: 12 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                flexWrap: "wrap",
                                gap: 1.5,
                              }}
                            >
                              <AppButton
                                variant="transparentPrimary"
                                label="Clear"
                                onClick={() => handleClearAllFilters()}
                                customStyles={{ minWidth: 140, height: 40 }}
                              />
                              <AppButton
                                variant="primary"
                                label="Apply"
                                onClick={handleApply}
                                disabled={loading}
                                customStyles={{ minWidth: 140, height: 40 }}
                              />
                            </Box>
                          </Grid2>
                          {(salesAnalyticsDashboardStore?.subTeams?.length ??
                            0) > 0 && (
                            <Grid2 size={12}>
                              <Box
                                sx={{
                                  pt: 1.5,
                                  mt: 0.5,
                                  borderTop: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 1.25,
                                  }}
                                >
                                  <CustomTypography
                                    variant="heading7"
                                    color={
                                      theme.palette.customColors
                                        .tableGrayHeadingColor
                                    }
                                    style={{
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Teams
                                  </CustomTypography>
                                  {(filters.team_ids?.length ?? 0) > 0 && (
                                    <CustomTypography
                                      variant="heading8"
                                      color={
                                        theme.palette.customColors.greyTextColor
                                      }
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {filters.team_ids!.length} selected
                                    </CustomTypography>
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {salesAnalyticsDashboardStore.subTeams.map(
                                    (team) => {
                                      const teamId = team.id.toString();
                                      const isSelected =
                                        filters.team_ids?.some(
                                          (item) => item.id === teamId,
                                        ) ?? false;
                                      return (
                                        <Chip
                                          key={teamId}
                                          label={team.name}
                                          onClick={() =>
                                            handleTeamToggle(teamId, team.name)
                                          }
                                          variant={
                                            isSelected ? "filled" : "outlined"
                                          }
                                          sx={{
                                            height: 32,
                                            px: 0.5,
                                            borderRadius: "16px",
                                            fontSize: "0.8rem",
                                            fontWeight: isSelected ? 600 : 500,
                                            cursor: "pointer",
                                            transition:
                                              "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
                                            backgroundColor: isSelected
                                              ? theme.palette.primary.main
                                              : theme.palette.background.paper,
                                            color: isSelected
                                              ? theme.palette.primary
                                                  .contrastText
                                              : theme.palette.text.secondary,
                                            borderColor: isSelected
                                              ? theme.palette.primary.main
                                              : theme.palette.divider,
                                            "&:hover": {
                                              backgroundColor: isSelected
                                                ? theme.palette.primary.dark
                                                : theme.palette.action.hover,
                                              borderColor: isSelected
                                                ? theme.palette.primary.dark
                                                : theme.palette.primary.light,
                                            },
                                            "& .MuiChip-label": {
                                              px: 1.25,
                                            },
                                          }}
                                        />
                                      );
                                    },
                                  )}
                                </Box>
                              </Box>
                            </Grid2>
                          )}
                        </Grid2>
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>

                {userStore.hasPermission(EmployeeRoles.ALL) && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                    }}
                  >
                    <AppButton
                      variant="primary"
                      label="Download Template"
                      startIcon={<Download />}
                      onClick={handleDownloadTemplate}
                      disabled={loading}
                      customStyles={{ minWidth: 140, height: 40 }}
                    />
                    <Box sx={{ position: "relative" }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleUploadFile}
                        style={{ display: "none" }}
                      />
                      <AppButton
                        variant="primary"
                        label="Upload Targets"
                        onClick={handleUploadButtonClick}
                        disabled={loading || uploading}
                        customStyles={{ minWidth: 140, height: 40 }}
                      />
                    </Box>
                  </Box>
                )}

                {((!isFiltersExpanded && (filters.team_ids?.length ?? 0) > 0) ||
                  selectedFiltersData.length > 0) && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        marginBottom: 10,
                      }}
                    >
                      Applied filters
                    </CustomTypography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      {!isFiltersExpanded &&
                        (filters.team_ids ?? []).map((team) => (
                          <Chip
                            key={`team-${team.id}`}
                            size="small"
                            label={team.value}
                            onDelete={() => handleTeamFilterRemove(team.id)}
                            sx={{
                              fontSize: "0.8rem",
                              height: 28,
                              fontWeight: 500,
                              backgroundColor:
                                theme.palette.primary.main + "14",
                              color: theme.palette.primary.main,
                              border: `1px solid ${theme.palette.primary.main}40`,
                              "& .MuiChip-deleteIcon": {
                                color: theme.palette.primary.main,
                                "&:hover": {
                                  color: theme.palette.primary.dark,
                                },
                              },
                            }}
                          />
                        ))}
                      {selectedFiltersData.map((filter, index) => (
                        <Chip
                          key={`owner-${filter.value}-${index}`}
                          size="small"
                          label={filter.value}
                          onDelete={() => handleRemoveFilter(index)}
                          sx={{
                            fontSize: "0.8rem",
                            height: 28,
                            fontWeight: 500,
                            backgroundColor: theme.palette.action.hover,
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
              <Box sx={{ marginBottom: 3 }}>
                <CustomTypography
                  variant="heading8"
                  color={theme.palette.customColors.blackBorder}
                  style={{ margin: "12px 10px 0px 0px" }}
                >
                  <span style={{ fontStyle: "italic" }}>Note:</span> The
                  analytics data is cached. Recent changes may take up to 30
                  minutes to reflect.
                </CustomTypography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  gap: 4,
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                }}
              >
                <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        gap: 4,
                      }}
                    >
                      <Card
                        sx={{
                          width: { xs: "100vw", lg: "47vw" },
                          padding: "8px",
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          {/* Top row: icon + label on left, value on right */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <img
                                src={totalRevenueicon}
                                alt="Total Revenue"
                                height={40}
                                width={40}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <CustomTypography
                                  variant="heading5"
                                  color={
                                    theme.palette.customColors.greyTextColor
                                  }
                                >
                                  Total Revenue
                                </CustomTypography>
                                <Box
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.customColors
                                          .lightGreyColor,
                                    },
                                  }}
                                  onClick={() => {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Sales Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/revenue&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  }}
                                >
                                  <CustomTypography variant="heading1">
                                    {salesAnalyticsDashboardStore?.response
                                      ?.targets_and_achievements
                                      ? "₹" +
                                        CurrencyHelper.formatIndianNumber(
                                          Number(
                                            salesAnalyticsDashboardStore?.response?.targets_and_achievements
                                              ?.filter(
                                                (item) =>
                                                  item.category_name ===
                                                  "Revenue",
                                              )[0]
                                              ?.achieved_value?.toFixed(2),
                                          ),
                                        )
                                      : 0}
                                  </CustomTypography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>

                          {/* Divider */}
                          <Box
                            sx={{
                              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
                            }}
                          />

                          {/* Bottom row: incentives in Updated Time style */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {[
                              {
                                label: "Outbound Incentives",
                                value:
                                  salesAnalyticsDashboardStore?.response
                                    ?.incentives?.outbound_incentive,
                              },
                              {
                                label: "Incentives",
                                value:
                                  salesAnalyticsDashboardStore?.response
                                    ?.incentives?.incentive,
                              },
                              {
                                label: "Total Incentives",
                                value:
                                  salesAnalyticsDashboardStore?.response
                                    ?.incentives?.total_incentive,
                              },
                            ].map(({ label, value }) => {
                              const score =
                                salesAnalyticsDashboardStore?.overallPerformance ||
                                0;
                              const borderColor = getGradientColorAtScore(
                                score,
                                performanceGradientColors,
                              );

                              return (
                                <Box
                                  key={label}
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    borderLeft: `4px solid ${borderColor}`,
                                    pl: 1,
                                    gap: 1,
                                  }}
                                >
                                  <CustomTypography variant="heading8">
                                    {label}
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="heading6"
                                    color={
                                      theme.palette.customColors
                                        .tableGrayHeadingColor
                                    }
                                  >
                                    {value != null
                                      ? `₹${CurrencyHelper.formatIndianNumber(Number(value.toFixed(2)))}`
                                      : "-"}
                                  </CustomTypography>
                                </Box>
                              );
                            })}
                          </Box>

                          <CustomTypography variant="body2">
                            ** Incentive values shown here are always based on
                            the latest data. Updates every 2 hours.
                          </CustomTypography>
                        </CardContent>
                      </Card>

                      {/* <Card
                      sx={{
                        width: { xs: "100vw", lg: "47vw" },
                        padding: "8px",
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          flexDirection: "column",
                          alignItems: "start",
                        }}
                      >
                        <CustomTypography variant="heading5">
                          Updated Time
                        </CustomTypography>

                        {salesAnalyticsDashboardStore?.response?.updated_time &&
                          Object.entries(
                            salesAnalyticsDashboardStore.response.updated_time,
                          ).map(([entity_name, last_updated_at], index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                borderLeft: `4px solid ${theme.palette.customColors.purple}`,
                                pl: 2,
                                gap: 0.5,
                                mt: 3,
                              }}
                            >
                              <CustomTypography variant="heading7">
                                {entity_name?.charAt(0)?.toUpperCase() +
                                  entity_name?.slice(1)}{" "}
                              </CustomTypography>
                              <CustomTypography
                                variant="body3"
                                color={
                                  theme.palette.customColors
                                    .tableGrayHeadingColor
                                }
                              >
                                {dayjs(last_updated_at).format(
                                  "DD MMM, YYYY [at] HH:mm:ss",
                                )}{" "}
                              </CustomTypography>
                            </Box>
                          ))}
                      </CardContent>
                    </Card> */}
                    </Box>

                    <Card>
                      <CardContent sx={{ px: 3, py: 3 }}>
                        <CustomTypography
                          variant="heading5"
                          style={{ marginBottom: 16 }}
                        >
                          Targets vs Achievements
                        </CustomTypography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            px: 1,
                            pb: 2,
                            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            color={theme.palette.customColors.greyTextColor}
                            style={{ width: "40%" }}
                          >
                            Categories
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            color={theme.palette.customColors.greyTextColor}
                            style={{ width: "30%", textAlign: "center" }}
                          >
                            Targets
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            color={theme.palette.customColors.greyTextColor}
                            style={{ width: "20%", textAlign: "center" }}
                          >
                            Achieved
                          </CustomTypography>
                        </Box>

                        {salesAnalyticsDashboardStore?.response?.targets_and_achievements?.map(
                          (row, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                py: 1,
                                px: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "40%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mr: 2,
                                    "@media (max-width: 430px)": {
                                      display: "none",
                                    },
                                  }}
                                >
                                  <img
                                    src={iconMock[idx]}
                                    alt={`icon-${idx}`}
                                    width={44}
                                    height={44}
                                  />
                                </Box>

                                <CustomTypography
                                  variant="body1"
                                  style={{ fontWeight: 500 }}
                                >
                                  {row.category_name?.split(" ")[0] || "-"}
                                </CustomTypography>
                              </Box>

                              <Box>
                                <CustomTypography
                                  variant="body1"
                                  style={{
                                    fontWeight: 700,
                                    width: "30%",
                                    textAlign: "center",
                                  }}
                                >
                                  {row.category_name === "Revenue"
                                    ? CurrencyHelper.formatIndianNumber(
                                        row.total_target,
                                      )
                                    : row.total_target}
                                </CustomTypography>
                              </Box>

                              <Box
                                sx={{
                                  width: "20%",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.customColors.lightGreyColor,
                                  },
                                }}
                                onClick={() => {
                                  if (row.category_name === "Revenue") {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Revenue Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/revenue&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  } else if (
                                    row.category_name === "F2F Meeting"
                                  ) {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Meetings Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/meetings&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  } else if (
                                    row.category_name === "Inbound Count"
                                  ) {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Inbound Leads Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/inbound-leads&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  } else if (
                                    row.category_name === "Outbound Count"
                                  ) {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Outbound Leads Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/outbound-leads&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  } else if (row.category_name === "F&G Std") {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "F&amp;G Std Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/function-and-gold-standard&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  }
                                }}
                              >
                                <CustomTypography
                                  variant="body1"
                                  style={{
                                    width: "20%",
                                    fontWeight: 700,
                                    textAlign: "center",
                                  }}
                                >
                                  {row.category_name === "Revenue"
                                    ? CurrencyHelper.formatIndianNumber(
                                        row.achieved_value,
                                      )
                                    : row.achieved_value % 1 !== 0
                                      ? row.achieved_value.toFixed(2)
                                      : row.achieved_value}
                                </CustomTypography>
                              </Box>
                            </Box>
                          ),
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: { xs: "100%", lg: "48%", xl: "48%" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <Card sx={{ minHeight: 470, padding: "8px" }}>
                    <CardContent>
                      <CustomTypography variant="heading5">
                        Overall Performance
                      </CustomTypography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          paddingTop: 5,
                        }}
                      >
                        <CustomCircularProgress
                          size={250}
                          thickness={4}
                          progress={
                            salesAnalyticsDashboardStore?.overallPerformance ||
                            0
                          }
                          label="Overall"
                          bgColor={theme.palette.customColors.lightBlue}
                          progressColor={getGradientColorAtScore(
                            salesAnalyticsDashboardStore?.overallPerformance ||
                              0,
                            performanceGradientColors,
                          )}
                          labelColor={theme.palette.customColors.greyTextColor}
                          PercentFontSize={"1.5rem"}
                        />
                      </Box>
                      <Box mt={5}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            flexWrap: {
                              xs: "wrap", // wrap on small screens (below 769px)
                              md: "nowrap", // no wrap on medium (≥769px)
                            },
                            justifyContent: "center",
                          }}
                        >
                          {salesAnalyticsDashboardStore?.performance?.map(
                            (metric, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <CustomCircularProgress
                                  size={60}
                                  thickness={4}
                                  progress={
                                    metric.value % 1 !== 0
                                      ? parseFloat(metric.value.toFixed(2))
                                      : metric.value
                                  }
                                  bgColor={
                                    pieColors1[index % pieColors1.length]
                                  }
                                  progressColor={
                                    pieColors[index % pieColors.length]
                                  }
                                  PercentFontSize={"0.7rem"}
                                />
                                <CustomTypography
                                  variant="heading8"
                                  style={{
                                    marginTop: 5,
                                    fontSize: "0.75rem",
                                    textAlign: "center",
                                  }}
                                >
                                  {metric.category?.split(" ")[0] || "-"}
                                </CustomTypography>
                              </Box>
                            ),
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  <Card sx={{ minHeight: 180, padding: "8px" }}>
                    <CardContent>
                      <Box>
                        <CustomTypography
                          variant="heading5"
                          style={{ paddingBottom: 20 }}
                        >
                          Key Performance Indicator
                        </CustomTypography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        {salesAnalyticsDashboardStore?.response
                          ?.key_performance_indicators &&
                          Object.entries(
                            salesAnalyticsDashboardStore.response
                              .key_performance_indicators,
                          ).map(([key, item], index) => (
                            <Box
                              key={index}
                              flex={1}
                              textAlign="center"
                              sx={{
                                borderLeft:
                                  index !== 0
                                    ? `1px solid ${theme.palette.customColors.greyBorder}`
                                    : "none",
                              }}
                            >
                              <img
                                src={stepImagesInventoryOpsTat[index]}
                                alt={item.label || key}
                                style={{ width: 34, height: 34 }}
                              />

                              <CustomTypography
                                variant="body2"
                                color={theme.palette.customColors.greyTextColor}
                              >
                                {item.label || key}
                              </CustomTypography>
                              <Box
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.customColors.lightGreyColor,
                                  },
                                }}
                                onClick={() => {
                                  if (key === "first_call_tat") {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "First Call TAT Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/first-note&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  } else if (key === "sales_cycle") {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Sales Cycle Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/sales-cycle&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  } else if (key === "avg_discount") {
                                    const report_url = `/report/data?title=${encodeURI(
                                      "Avg Discount Report",
                                    )}&base_url=/v5/sales-analytics&path=/data/avg-discount&${searchParams.toString()}`;
                                    window.open(report_url, "_blank");
                                  }
                                }}
                              >
                                <CustomTypography
                                  variant="heading4"
                                  style={{ paddingTop: 5 }}
                                >
                                  {item?.value
                                    ? key === "avg_discount"
                                      ? `${item.value}%`
                                      : item.value
                                    : "-"}
                                </CustomTypography>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              <Card sx={{ marginTop: 4, paddingX: 4, paddingY: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <CustomTypography variant="heading4">
                      Leads Pipelines
                    </CustomTypography>
                  </Box>
                  <Box>
                    <Button
                      color="primary"
                      size="small"
                      style={{
                        textTransform: "none",
                      }}
                      onClick={() => {
                        const report_url = `/report/data?title=${encodeURI(
                          "All Leads Report",
                        )}&base_url=/v5/sales-analytics&path=/data/all-leads&${searchParams.toString()}`;
                        window.open(report_url, "_blank");
                      }}
                    >
                      View All
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    overflowX: "auto",
                    gap: 0.2,
                  }}
                >
                  <ColumnData
                    heading="Closed Orders"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.leadsPipelines[1]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.leadsPipelines[1]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("leads", 1, newPage)
                    }
                    cardId="1"
                    totalPages={
                      salesAnalyticsDashboardStore?.leadsPipelines[1]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.leadsPipelines[1]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.leadsPipelines[1]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.leadsPipelines[1]
                        ?.total_count || 0
                    }
                    type="lead"
                  />
                  <ColumnData
                    heading="100% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.leadsPipelines[100]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.leadsPipelines[100]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("leads", 100, newPage)
                    }
                    cardId="100%"
                    totalPages={
                      salesAnalyticsDashboardStore?.leadsPipelines[100]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.leadsPipelines[100]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.leadsPipelines[100]
                        ?.limit || 30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.leadsPipelines[100]
                        ?.total_count || 0
                    }
                    type="lead"
                  />
                  <ColumnData
                    heading="75% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.leadsPipelines[75]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.leadsPipelines[75]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("leads", 75, newPage)
                    }
                    cardId="75%"
                    totalPages={
                      salesAnalyticsDashboardStore?.leadsPipelines[75]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.leadsPipelines[75]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.leadsPipelines[75]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.leadsPipelines[75]
                        ?.total_count || 0
                    }
                    type="lead"
                  />
                  <ColumnData
                    heading="50% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.leadsPipelines[50]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.leadsPipelines[50]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("leads", 50, newPage)
                    }
                    cardId="50%"
                    totalPages={
                      salesAnalyticsDashboardStore?.leadsPipelines[50]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.leadsPipelines[50]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.leadsPipelines[50]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.leadsPipelines[50]
                        ?.total_count || 0
                    }
                    type="lead"
                  />
                  <ColumnData
                    heading="25% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.leadsPipelines[25]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.leadsPipelines[25]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("leads", 25, newPage)
                    }
                    cardId="25%"
                    totalPages={
                      salesAnalyticsDashboardStore?.leadsPipelines[25]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.leadsPipelines[25]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.leadsPipelines[25]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.leadsPipelines[25]
                        ?.total_count || 0
                    }
                    type="lead"
                  />
                  <ColumnData
                    heading="0% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.leadsPipelines[0]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.leadsPipelines[0]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("leads", 0, newPage)
                    }
                    cardId="0%"
                    totalPages={
                      salesAnalyticsDashboardStore?.leadsPipelines[0]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.leadsPipelines[0]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.leadsPipelines[0]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.leadsPipelines[0]
                        ?.total_count || 0
                    }
                    type="lead"
                  />
                </Box>
              </Card>

              <Card
                sx={{ marginTop: 4, paddingX: 4, paddingY: 3, marginBottom: 3 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <CustomTypography variant="heading4">
                      Deals Pipelines
                    </CustomTypography>
                  </Box>
                  <Box>
                    <Button
                      color="primary"
                      size="small"
                      style={{
                        textTransform: "none",
                      }}
                      onClick={() => {
                        const report_url = `/report/data?title=${encodeURI(
                          "All Deals Report",
                        )}&base_url=/v5/sales-analytics&path=/data/all-deals&${searchParams.toString()}`;
                        window.open(report_url, "_blank");
                      }}
                    >
                      View All
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    overflowX: "auto",
                    gap: 0.2,
                  }}
                >
                  <ColumnData
                    heading="Closed Orders"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.dealsPipelines[1]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.dealsPipelines[1]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("deals", 1, newPage)
                    }
                    cardId="1"
                    totalPages={
                      salesAnalyticsDashboardStore?.dealsPipelines[1]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.dealsPipelines[1]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.dealsPipelines[1]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.dealsPipelines[1]
                        ?.total_count || 0
                    }
                    type="deal"
                  />
                  <ColumnData
                    heading="100% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.dealsPipelines[100]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.dealsPipelines[100]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("deals", 100, newPage)
                    }
                    cardId="100%"
                    totalPages={
                      salesAnalyticsDashboardStore?.dealsPipelines[100]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.dealsPipelines[100]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.dealsPipelines[100]
                        ?.limit || 30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.dealsPipelines[100]
                        ?.total_count || 0
                    }
                    type="deal"
                  />
                  <ColumnData
                    heading="75% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.dealsPipelines[75]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.dealsPipelines[75]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("deals", 75, newPage)
                    }
                    cardId="75%"
                    totalPages={
                      salesAnalyticsDashboardStore?.dealsPipelines[75]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.dealsPipelines[75]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.dealsPipelines[75]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.dealsPipelines[75]
                        ?.total_count || 0
                    }
                    type="deal"
                  />
                  <ColumnData
                    heading="50% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.dealsPipelines[50]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.dealsPipelines[50]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("deals", 50, newPage)
                    }
                    cardId="50%"
                    totalPages={
                      salesAnalyticsDashboardStore?.dealsPipelines[50]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.dealsPipelines[50]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.dealsPipelines[50]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.dealsPipelines[50]
                        ?.total_count || 0
                    }
                    type="deal"
                  />
                  <ColumnData
                    heading="25% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.dealsPipelines[25]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.dealsPipelines[25]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("deals", 25, newPage)
                    }
                    cardId="25%"
                    totalPages={
                      salesAnalyticsDashboardStore?.dealsPipelines[25]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.dealsPipelines[25]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.dealsPipelines[25]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.dealsPipelines[25]
                        ?.total_count || 0
                    }
                    type="deal"
                  />
                  <ColumnData
                    heading="0% Committed"
                    amount={`₹${formatNumber(
                      salesAnalyticsDashboardStore?.dealsPipelines[0]?.amount,
                    )}`}
                    list={
                      salesAnalyticsDashboardStore?.dealsPipelines[0]?.list ||
                      []
                    }
                    onPageChange={(newPage) =>
                      handlePipelinePageChange("deals", 0, newPage)
                    }
                    cardId="0%"
                    totalPages={
                      salesAnalyticsDashboardStore?.dealsPipelines[0]
                        ?.total_pages || 0
                    }
                    pageNo={
                      salesAnalyticsDashboardStore?.dealsPipelines[0]
                        ?.page_no || 1
                    }
                    itemsPerPage={
                      salesAnalyticsDashboardStore?.dealsPipelines[0]?.limit ||
                      30
                    }
                    totalCount={
                      salesAnalyticsDashboardStore?.dealsPipelines[0]
                        ?.total_count || 0
                    }
                    type="deal"
                  />
                </Box>
              </Card>

              {/* Owner Sales Table Card */}
              <Card
                sx={{
                  height: "500px",
                  marginTop: 4,
                  paddingX: 4,
                  paddingY: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 1,
                    marginBottom: 1,
                  }}
                >
                  <CustomTypography variant="heading4">
                    Owner-wise Revenue & Leads Counts
                  </CustomTypography>
                </Box>
                <CardContent
                  sx={{
                    flex: "1 1 0%",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      flex: "1 1 0%",
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    <OwnerSalesRowTable
                      data={
                        salesAnalyticsDashboardStore.response
                          ?.owner_sales_rows || []
                      }
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Incentives Table Card */}
              <Card
                sx={{
                  height: "500px",
                  marginTop: 4,
                  paddingX: 4,
                  paddingY: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 1,
                    marginBottom: 1,
                  }}
                >
                  <CustomTypography variant="heading4">
                    Incentives Data
                  </CustomTypography>
                </Box>
                <CardContent
                  sx={{
                    flex: "1 1 0%",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      flex: "1 1 0%",
                      height: "100%",
                      overflow: "auto",
                    }}
                  >
                    <SalesIncentivesRowTable
                      data={salesAnalyticsDashboardStore.incentivesData || []}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid2>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(SalesAnalyticsDashboard);
